import React from 'react';
import { useAuth } from '../../constants/providers/AuthProvider';
import { SidebarProvider } from '../../context/SidebarContext';
import { SidebarDataProvider } from '../../context/SidebarDataContext';

interface AuthenticatedLayoutWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper that provides the sidebar providers around every route.
 *
 * The providers are rendered for signed-out visitors too, and that is
 * load-bearing: the element tree around `children` must not change shape
 * when a session appears. Swapping `children` for `<SidebarProvider>{children}`
 * on sign-in made React unmount and remount the entire router subtree, which
 * wiped any in-progress page state. GuidedStudentOnboarding signs the user up
 * mid-wizard, so the remount sent invited students back to the date-of-birth
 * step after they had already created their account (the "DOB → password →
 * DOB again" loop). SidebarDataProvider refetches when the signed-in user
 * changes, so it does not need a remount to pick up the new session.
 */
export const AuthenticatedLayoutWrapper: React.FC<AuthenticatedLayoutWrapperProps> = ({ children }) => {
  const { loading: authLoading } = useAuth();

  // Show loading state while determining auth status on first load
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#111010] text-[#EBFFEE]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
          <p className="mt-2 text-[#A9A3A3]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <SidebarDataProvider>
        {children}
      </SidebarDataProvider>
    </SidebarProvider>
  );
};